import styled, { css } from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Button, DialogActions } from '@mui/material';
import { Close } from '@mui/icons-material';

import { formatSum, getLeadsLimit, useAppDispatch, useAppSelector } from '../../lib';
import { closeTarifInfoPopup } from '../../store';
import { tarifsDescription } from './tarifs-description';
import { UserPlan } from '../../types';
import { api } from '../../api';

type PayPeriod = 'yearly' | 'monthly';

const getFeatureColor = (text?: string): string => {
  if (!text) {
    return '';
  }

  if (text.includes('✘')) {
    return '#EE4E34';
  }

  if (text.includes('✔')) {
    return '#2BAE66';
  }

  return '#37352f';
};

const getLeadsLimitText = (tarifName: UserPlan): string => {
  if (tarifName === 'premium') {
    return '✔ Нет лимитов на лиды';
  }

  return `До ${getLeadsLimit(tarifName)} лидов в день`;
};

const getPaymentSummary = (tarifName: UserPlan, period: PayPeriod) => {
  const price = tarifsDescription[tarifName].price[period];

  if (period === 'monthly') {
    return formatSum(price);
  }

  return formatSum(price * 12);
};

export const TarifInfoPopup = () => {
  const [createPaymentLink, createPaymentLinkResult] = api.useCreatePaymentLinkMutation();
  const dispatch = useAppDispatch();
  const visible = useAppSelector((state) => state.app.tarifInfoPopupVisible);
  const [period, setPeriod] = useState<PayPeriod>('yearly');
  const [tarif, setTarif] = useState<UserPlan>('free');

  const handleModalClose = useCallback(() => {
    dispatch(closeTarifInfoPopup());
  }, [dispatch]);

  const handleConfirm = async () => {
    createPaymentLink({ tarif, period });
  };

  useEffect(() => {
    if (createPaymentLinkResult?.data?.url) {
      window.open(createPaymentLinkResult.data.url, '_self');
    }
  }, [createPaymentLinkResult.data]);

  return (
    <Dialog open={visible} onClose={handleModalClose} maxWidth="lg">
      <DialogTitle>
        <span>Тарифные планы</span>

        <CloseModalButton onClick={handleModalClose}>
          <Close />
        </CloseModalButton>
      </DialogTitle>

      <DialogContent>
        <ViewSwitch>
          <ViewSwitchButton
            size="small"
            selected={period === 'yearly'}
            onClick={() => {
              setPeriod('yearly');
            }}
          >
            Ежегодная оплата
          </ViewSwitchButton>

          <ViewSwitchButton
            size="small"
            selected={period === 'monthly'}
            onClick={() => {
              setPeriod('monthly');
            }}
          >
            Помесячная оплата
          </ViewSwitchButton>
        </ViewSwitch>

        <CardsWrapper>
          {Object.entries(tarifsDescription).map(([tarifName, tarifDescription]) => {
            const price = tarifDescription.price[period];
            const limitText = getLeadsLimitText(tarifName as UserPlan);
            const isFreeTarif = tarifName === 'free';

            return (
              <Card
                key={tarifName}
                selectable={!isFreeTarif}
                selected={!isFreeTarif && tarifName === tarif}
                onClick={() => {
                  if (!isFreeTarif) {
                    setTarif(tarifName as UserPlan);
                  }
                }}
              >
                <Typography variant="caption">{tarifName}</Typography>

                <Typography variant="h6" sx={{ marginBottom: '12px' }}>
                  {price === 0 ? 'Бесплатно' : `${price} ₽ / мес`}
                </Typography>

                <TarifFeatures>
                  {tarifDescription.features.map((feature, index) => (
                    <TarifFeature key={index} color={getFeatureColor(feature?.toString())}>
                      {feature}
                    </TarifFeature>
                  ))}

                  <TarifFeature color={getFeatureColor(limitText)}>{limitText}</TarifFeature>
                </TarifFeatures>
              </Card>
            );
          })}
        </CardsWrapper>
      </DialogContent>

      <DialogActions>
        <Total hidden={tarif === 'free'}>
          <Typography variant="body2" variantMapping={{ body1: 'span' }}>
            К оплате:{' '}
            <Typography variant="body1" variantMapping={{ body1: 'span' }} fontWeight={500}>
              {getPaymentSummary(tarif, period)} ₽
            </Typography>
          </Typography>

          <Button variant="contained" onClick={handleConfirm} disabled={createPaymentLinkResult.isLoading}>
            Оплатить
          </Button>
        </Total>
      </DialogActions>
    </Dialog>
  );
};

const CloseModalButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const ViewSwitch = styled.div`
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 12px;
`;

const ViewSwitchButton = styled(IconButton)<{ selected?: boolean }>`
  border-radius: 0;
  padding: 4px 16px;
  border: 1px solid #3899ec;
  color: #3899ec;
  margin-bottom: 16px;

  :first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    border-right: 0;
  }

  :last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    border-left: 0;
  }

  :hover {
    background-color: '#f3f3f3';
  }

  ${(p) =>
    p.selected &&
    `
      background-color: #3899ec;
      border-color: #3899ec;
      color: #fff;

      :hover {
        background-color: #3899ec;
      }
    `}
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const Card = styled.div<{ selectable: boolean; selected: boolean }>`
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 12px 16px;
  width: 275px;
  transition: background-color 0.2s ease;

  ${(p) =>
    p.selected &&
    css`
      border-color: #7c7c7c;
      box-shadow: 0 0 10px #00000040;
    `};

  :hover {
    ${(p) =>
      p.selectable &&
      css`
        cursor: pointer;
        background-color: #f3f3f3;
      `};
  }
`;

const TarifFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
`;

const TarifFeature = styled.li<{ color: string }>`
  margin-bottom: 8px;

  color: ${(p) => p.color};
`;

const Total = styled.div<{ hidden: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;

  ${(p) =>
    p.hidden &&
    css`
      visibility: hidden;
    `};
`;
